import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PAPER_BASE } from './config';
import PaperSEO from '../../components/paper/PaperSEO';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const ITEMS_PER_PAGE = 12;

const CATEGORY_LABEL = {
  FESTIVAL: '축제',
  EVENT: '공연',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${dd}`;
};

export default function PaperBlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/paperBlog`, { params: { page: currentPage, limit: ITEMS_PER_PAGE } })
      .then(res => {
        setBlogs(res.data.blogs || []);
        setTotalPages(res.data.totalPages || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen">
      <PaperSEO
        title="비하인드 스토리"
        description="프로젝트의 뒷이야기를 들려드립니다"
        path="/blog"
      />

      {/* Header */}
      <section className="pt-10 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h1 className="text-white text-2xl font-bold mb-1">비하인드 스토리</h1>
          <p className="text-slate-500 text-sm">프로젝트의 뒷이야기를 들려드립니다</p>
        </div>
      </section>

      {/* Card Grid */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin" />
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-20 text-white/30">아직 작성된 스토리가 없습니다</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogs.map((blog) => (
                <Link
                  key={blog.id}
                  to={`${PAPER_BASE}/blog/${blog.id}`}
                  className="group bg-[#141414] rounded-2xl border border-white/5 hover:border-white/15 hover:shadow-xl overflow-hidden transition-all duration-300"
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video overflow-hidden">
                    {blog.thumbnailUrl ? (
                      <img
                        src={blog.thumbnailUrl}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-violet-900/40 to-fuchsia-900/20" />
                    )}
                    {blog.work && (
                      <span className="absolute top-3 left-3 bg-gradient-to-r from-violet-600/90 to-fuchsia-500/90 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm shadow-lg">
                        {CATEGORY_LABEL[blog.work.category] || blog.work.category}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-white text-base font-semibold mb-2 line-clamp-2 group-hover:text-violet-300 transition-colors">
                      {blog.title}
                    </h3>
                    {blog.summary && (
                      <p className="text-white/40 text-xs leading-relaxed line-clamp-2">
                        {blog.summary}
                      </p>
                    )}
                    {(blog.work?.workDate || blog.publishedAt) && (
                      <p className="text-white/25 text-xs mt-3">
                        {formatDate(blog.work?.workDate || blog.publishedAt)}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <section className="pb-32">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-center gap-3">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-4 py-2 rounded-full border border-white/10 text-white/40 hover:border-violet-400/30 hover:text-violet-400 disabled:opacity-20 disabled:cursor-not-allowed transition-all text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              이전
            </button>
            <span className="text-white/30 text-sm">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-4 py-2 rounded-full border border-white/10 text-white/40 hover:border-violet-400/30 hover:text-violet-400 disabled:opacity-20 disabled:cursor-not-allowed transition-all text-sm"
            >
              다음
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
